/**
 * Policy Library — List, search, filter, create, and upload policies.
 * Supports AI-powered document parsing from PDF and DOCX uploads.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useImpersonation from '../../hooks/useImpersonation';
import BulkImport from './BulkImport';
import { DocumentPlusIcon, DocumentDuplicateIcon, BookOpenIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const STATUS_CFG = {
  draft:        { label: 'Draft',       color: 'bg-slate-100 text-slate-600 border-slate-200' },
  active:       { label: 'Active',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  under_review: { label: 'Under Review',color: 'bg-amber-50 text-amber-700 border-amber-200' },
  superseded:   { label: 'Superseded',  color: 'bg-slate-50 text-slate-500 border-slate-200' },
  archived:     { label: 'Archived',    color: 'bg-slate-50 text-slate-400 border-slate-200' },
};

const CATEGORY_OPTIONS = ['compliance', 'operational', 'clinical', 'privacy', 'financial', 'it', 'vendor', 'other'];

// Spreadsheet columns. Policy ID + Title are always shown; the rest are
// toggleable so the default view stays readable instead of an 11-column wall.
// The choice persists per browser via localStorage.
const COLUMN_DEFS = [
  { key: 'status',      label: 'Status' },
  { key: 'category',    label: 'Category' },
  { key: 'department',  label: 'Dept. Owner' },
  { key: 'owner',       label: 'Policy Owner' },
  { key: 'effective',   label: 'Effective' },
  { key: 'revision',    label: 'Revision' },
  { key: 'next_review', label: 'Next Review' },
  { key: 'approval',    label: 'Approval' },
  { key: 'org_fields',  label: 'Custom fields' },
  { key: 'file',        label: 'File' },
];
// Operator decision 2026-07-22 (for Solis): the business files by DEPARTMENT,
// not category — Department is in the default view, Category moves to the
// optional column picker. Storage key bumped so browsers holding the old
// default pick up the new one.
const DEFAULT_COLUMNS = ['status', 'department', 'owner', 'next_review', 'file'];
const COLUMNS_STORAGE_KEY = 'ppm_library_columns_v2';

function loadStoredColumns() {
  try {
    const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_COLUMNS;
    const valid = arr.filter(k => COLUMN_DEFS.some(c => c.key === k));
    return valid.length ? valid : DEFAULT_COLUMNS;
  } catch { return DEFAULT_COLUMNS; }
}

export default function PolicyLibrary({ onOpenPolicy }) {
  // SOC 2: writes are blocked server-side during impersonation; mirror that
  // in the UI so the buttons themselves are honest about what's possible.
  const { active: impersonating } = useImpersonation();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [reviewStateFilter, setReviewStateFilter] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [orgFieldFilters, setOrgFieldFilters] = useState({}); // { field_key: value }
  const [filterOptions, setFilterOptions] = useState({ categories: [], departments: [], owners: [], filterable_fields: [] });
  const [allOrgFields, setAllOrgFields] = useState([]);  // every defined field (for pill labels + full filter drawer)
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [visibleCols, setVisibleCols] = useState(loadStoredColumns);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);

  const toggleColumn = (key) => {
    setVisibleCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      try { window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Upload states
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [parsedPolicy, setParsedPolicy] = useState(null);
  const [uploadForm, setUploadForm] = useState(null);
  const [orgFieldInputs, setOrgFieldInputs] = useState({});  // { field_def_id: value }
  const [templateDecision, setTemplateDecision] = useState(null); // { action, template_id?, new_template_name? }
  const [newTemplateName, setNewTemplateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Create form
  const [form, setForm] = useState({ policy_number: '', title: '', description: '', category: 'compliance', department_id: '', owner_user_id: '', review_cycle_months: 12 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (categoryFilter) qs.set('category', categoryFilter);
      if (departmentFilter) qs.set('department_id', departmentFilter);
      if (ownerFilter) qs.set('owner_user_id', ownerFilter);
      if (reviewStateFilter) qs.set('review_state', reviewStateFilter);
      if (effectiveFrom) qs.set('effective_from', effectiveFrom);
      if (effectiveTo) qs.set('effective_to', effectiveTo);
      const url = `${API}/api/ppm/policies?${qs}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setPolicies(data.policies || []);
    } catch {}
    setLoading(false);
  }, [statusFilter, categoryFilter, departmentFilter, ownerFilter, reviewStateFilter, effectiveFrom, effectiveTo]);

  useEffect(() => { load(); }, [load]);

  // current_user is needed for smart-default placeholders (approved_by_name,
  // owner fallback). uploadMeta is the source of truth for the upload form's
  // department + user dropdowns, replacing the previously broken /api/users.
  const [currentUser, setCurrentUser] = useState(null);
  // Hint shown above the Department dropdown when the AI extracted a name
  // that doesn't match any department in the org.
  const [departmentHint, setDepartmentHint] = useState(null); // { name, failed }

  useEffect(() => {
    (async () => {
      try {
        const [metaRes, fRes, defRes] = await Promise.all([
          fetch(`${API}/api/ppm/upload-meta`, { credentials: 'include' }),
          fetch(`${API}/api/ppm/policies/filter-options`, { credentials: 'include' }),
          fetch(`${API}/api/ppm/org-fields/definitions`, { credentials: 'include' }),
        ]);
        const metaData = await metaRes.json();
        const fData = await fRes.json();
        const defData = await defRes.json();
        if (metaData.success) {
          setDepartments(metaData.departments || []);
          setUsers(metaData.users || []);
          setCurrentUser(metaData.current_user || null);
        }
        if (fData.success) setFilterOptions(fData);
        if (defData.success) setAllOrgFields(defData.definitions || []);
      } catch {}
    })();
  }, []);

  // Compliance-department rule: when the form's department is the
  // Compliance department, approval_date represents the Board of Directors
  // approval and must be entered manually. We reactively clear any
  // gray-dot (auto-defaulted) value and add today's date back when the
  // user switches to a non-Compliance department.
  useEffect(() => {
    if (!uploadForm) return;
    const dept = departments.find(d => d.id === uploadForm.department_id);
    const isCompliance = (dept?.name || '').toLowerCase() === 'compliance';
    // Switched TO compliance with a non-AI default still in place: clear it.
    if (isCompliance && uploadForm.approval_date && !uploadForm._ai_approval_date) {
      setUploadForm(f => ({ ...f, approval_date: '' }));
    }
    // Switched AWAY from compliance with the field empty and never edited:
    // restore the today default so non-compliance flows behave as before.
    if (!isCompliance && !uploadForm.approval_date && !uploadForm._ai_approval_date) {
      setUploadForm(f => ({ ...f, approval_date: new Date().toISOString().slice(0, 10) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadForm?.department_id]);

  // Eligible owners for the upload form's Policy Owner dropdown.
  //   1. Department selected AND has ≥1 active user → only those users
  //   2. Department selected but 0 users → fall back to all org users
  //   3. No department selected → all org users
  // The empty-list case (no users in the org at all) is handled in the JSX
  // with the "ask a Superuser" tooltip.
  const eligibleOwners = (() => {
    const deptId = uploadForm?.department_id;
    if (deptId) {
      const inDept = users.filter(u => u.department_id === deptId);
      if (inDept.length > 0) return { list: inDept, scope: 'department' };
    }
    return { list: users, scope: 'org' };
  })();

  const filtered = policies.filter(p => {
    // Operator decision 2026-07-22: archived policies stay OUT of the default
    // view — they clutter the working dashboard — but remain fully searchable.
    // They surface when the search box has text that matches them, or when the
    // status filter explicitly asks for Archived.
    if (p.status === 'archived' && statusFilter !== 'archived' && !search.trim()) return false;
    if (search) {
      const q = search.toLowerCase();
      const hit =
        (p.title || '').toLowerCase().includes(q) ||
        (p.policy_number || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q);
      if (!hit) return false;
    }
    // Org custom field filters (client-side against p.org_fields jsonb)
    for (const [key, val] of Object.entries(orgFieldFilters)) {
      if (!val) continue;
      if ((p.org_fields?.[key] || '') !== val) return false;
    }
    return true;
  });

  const activeFilterCount = [statusFilter, categoryFilter, departmentFilter, ownerFilter,
    reviewStateFilter, effectiveFrom, effectiveTo,
    ...Object.values(orgFieldFilters).filter(Boolean)].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter(''); setCategoryFilter(''); setDepartmentFilter('');
    setOwnerFilter(''); setReviewStateFilter('');
    setEffectiveFrom(''); setEffectiveTo('');
    setOrgFieldFilters({});
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.policy_number.trim() || !form.title.trim()) { setError('Policy number and title required'); return; }
    setCreating(true); setError('');
    try {
      const res = await fetch(`${API}/api/ppm/policies`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setShowCreate(false);
        setForm({ policy_number: '', title: '', description: '', category: 'compliance', department_id: '', owner_user_id: '', review_cycle_months: 12 });
        onOpenPolicy(data.policy.id);
      } else {
        setError(data.message || 'Create failed');
      }
    } catch (err) { setError('Create failed'); }
    setCreating(false);
  };

  // --- Upload handlers ---
  const handleFileUpload = async (file) => {
    if (!file) return;

    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'docx', 'doc', 'txt'].includes(ext)) {
      setUploadError('Unsupported file type. Please upload a PDF, DOCX, or TXT file.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setParsedPolicy(null);
    setUploadForm(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/ppm/policies/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setParsedPolicy(data);
        // Department resolution: backend looked up the AI-extracted name
        // against the org's departments table. If matched, we get a UUID;
        // if not, the field stays empty and the user picks from the dropdown
        // (with a hint showing what the AI thought it was).
        const resolvedDeptId = data.resolved_department_id || '';
        setDepartmentHint(
          data.department_match_failed && data.department_match_name
            ? { name: data.department_match_name, failed: true }
            : null
        );

        // Owner auto-selection: only auto-pick when the matched dept has
        // EXACTLY ONE active user. Multiple users → leave blank, dropdown
        // gets filtered to those users so the associate confirms which.
        // No matching dept → blank, fall back to all-org dropdown.
        const deptUsers = Array.isArray(data.department_users) ? data.department_users : [];
        const autoOwner = (resolvedDeptId && deptUsers.length === 1)
          ? deptUsers[0].id
          : '';

        // Smart defaults for fields the AI couldn't extract from the
        // document. Only used as placeholders — if the AI returned a value,
        // that value wins. The UI shows green-dot vs gray-dot to indicate
        // which is which.
        //
        // Compliance-department exception: approval_date for compliance
        // policies represents the Board of Directors approval date — it
        // is NOT a routine sign-off. Required to be entered manually by
        // the compliance associate, never auto-defaulted to today.
        const aiApprover = data.parsed.approved_by || '';
        const aiApprovalDate = data.parsed.approval_date || '';
        const fallbackApprover = data.current_user?.name || currentUser?.name || '';
        const todayIso = data.today || new Date().toISOString().slice(0, 10);
        const matchedDeptName = (departments.find(d => d.id === resolvedDeptId)?.name || '').toLowerCase();
        const isComplianceDept = matchedDeptName === 'compliance';
        const fallbackApprovalDate = isComplianceDept ? '' : todayIso;

        // Pre-fill upload form with ALL extracted canonical fields
        setUploadForm({
          upload_id: data.upload_id,  // carries the original file through to save
          policy_number: data.parsed.policy_number || '',
          title: data.parsed.title || '',
          description: data.parsed.description || '',
          category: data.parsed.category || 'compliance',
          department_id: resolvedDeptId,
          owner_user_id: autoOwner,
          review_cycle_months: 12,
          // Extended canonical metadata — now saved on confirm
          revision_number: data.parsed.revision_number || '',
          effective_date: data.parsed.effective_date || '',
          expiration_date: data.parsed.expiration_date || '',
          // Approver/approval-date use AI value if present, else the smart
          // default (uploader / today). Track _ai_extracted_* so the UI can
          // render the green/gray indicator.
          approved_by_name: aiApprover || fallbackApprover,
          approval_date: aiApprovalDate || fallbackApprovalDate,
          _ai_approved_by: !!aiApprover,
          _ai_approval_date: !!aiApprovalDate,
          _ai_department: !!resolvedDeptId,
          supersedes_policy_number: data.parsed.supersedes || '',
          key_sections: Array.isArray(data.parsed.key_sections) ? data.parsed.key_sections : [],
          // Content — verbatim raw extracted text, no AI round-trip
          content: data.parsed.content || '',
          change_summary: `Uploaded from ${file.name}`,
          cfr_citations: data.parsed.cfr_citations || [],
          file_name: file.name,
        });
        // Pre-fill org custom field inputs from AI extraction
        const ofInputs = {};
        for (const f of (data.parsed.extracted_org_fields || [])) {
          ofInputs[f.field_definition_id] = f.value;
        }
        setOrgFieldInputs(ofInputs);

        // Seed template decision from detection result
        const td = data.template_detection;
        if (td?.recommended_action === 'use_existing' && td.best_match) {
          setTemplateDecision({ action: 'use_existing', template_id: td.best_match.id });
        } else if (td?.recommended_action === 'save_new') {
          setTemplateDecision({ action: 'save_new' });
          setNewTemplateName('');
        } else {
          setTemplateDecision({ action: 'one_time' });
        }
      } else {
        setUploadError(data.message || 'Upload failed');
      }
    } catch (err) {
      setUploadError('Failed to upload file. Please try again.');
    }
    setUploading(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleConfirmUpload = async () => {
    if (!uploadForm?.policy_number?.trim() || !uploadForm?.title?.trim()) {
      setUploadError('Policy number and title are required');
      return;
    }
    // Compliance-department policies must have a Board of Directors
    // approval date entered manually — never auto-filled or skipped.
    {
      const dept = departments.find(d => d.id === uploadForm.department_id);
      const isCompliance = (dept?.name || '').toLowerCase() === 'compliance';
      if (isCompliance && !uploadForm.approval_date) {
        setUploadError('Board of Directors approval date is required for Compliance policies. Please enter it before saving.');
        return;
      }
    }
    setSaving(true);
    setUploadError('');

    try {
      // Build org_field_values from the inputs (drop empty ones)
      const orgFieldValues = Object.entries(orgFieldInputs)
        .filter(([, v]) => v != null && String(v).trim() !== '')
        .map(([field_definition_id, value]) => ({ field_definition_id, value: String(value) }));

      // Compose template_decision — agentic template flexibility
      let decision = templateDecision;
      if (decision?.action === 'save_new') {
        if (!newTemplateName.trim()) {
          setUploadError('Please name the new template or choose another template option.');
          setSaving(false);
          return;
        }
        decision = { ...decision, new_template_name: newTemplateName.trim() };
      }

      const res = await fetch(`${API}/api/ppm/policies/upload/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...uploadForm,
          org_field_values: orgFieldValues,
          template_decision: decision,
        }),
      });
      const data = await res.json();

      if (data.success) {
        setShowUpload(false);
        setParsedPolicy(null);
        setUploadForm(null);
        load();
        onOpenPolicy(data.policy.id);
      } else {
        setUploadError(data.message || 'Save failed');
      }
    } catch (err) {
      setUploadError('Failed to save policy');
    }
    setSaving(false);
  };

  const cancelUpload = () => {
    setShowUpload(false);
    setParsedPolicy(null);
    setUploadForm(null);
    setUploadError('');
    setUploading(false);
  };

  // Stats
  const activeCount = policies.filter(p => p.status === 'active').length;
  const reviewCount = policies.filter(p => p.status === 'under_review').length;
  const draftCount  = policies.filter(p => p.status === 'draft').length;
  const archivedCount = policies.filter(p => p.status === 'archived').length;

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800 tracking-tight">Policy Library</h2>
            <p className="text-slate-400 text-xs mt-0.5">
              {policies.length} {policies.length === 1 ? 'policy' : 'policies'} · {activeCount} active · {reviewCount} under review · {draftCount} draft
              {archivedCount > 0 && (
                <>
                  {' · '}
                  <button type="button" onClick={() => setStatusFilter(statusFilter === 'archived' ? '' : 'archived')}
                    className="text-slate-400 hover:text-slate-600 underline decoration-dotted underline-offset-2"
                    title="Archived policies are tucked out of the main view. Click to show them; search always finds them.">
                    {archivedCount} archived {statusFilter === 'archived' ? '(showing)' : '(hidden — click to show)'}
                  </button>
                </>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Single-policy Upload HIDDEN (not removed) per operator request
                2026-07-20 — Bulk Import is the only intake door for now.
                Flip SHOW_SINGLE_UPLOAD to restore. */}
            {false && (
            <button
              onClick={() => { setShowUpload(true); setShowCreate(false); }}
              disabled={impersonating}
              title={impersonating ? 'Read-only during impersonation' : ''}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Policy
            </button>
            )}
            <button
              onClick={() => { setShowBulkImport(true); setShowUpload(false); setShowCreate(false); }}
              disabled={impersonating}
              title={impersonating ? 'Read-only during impersonation' : 'Import your whole policy manual at once — drop files or folders, review every extracted date and title in a grid, then import.'}
              className="px-5 py-2.5 rounded-xl border-2 border-orange-500 text-orange-700 text-sm font-semibold hover:bg-orange-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
              </svg>
              Bulk Import
            </button>
            <button
              onClick={() => { setShowCreate(true); setShowUpload(false); }}
              disabled={impersonating}
              title={impersonating ? 'Read-only during impersonation' : ''}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + New Policy
            </button>
          </div>
        </div>

        {/* Upload Section */}
        {showUpload && (
          <div className="mb-6 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-800">Upload Policy Document</h3>
              <button onClick={cancelUpload} className="text-slate-400 hover:text-slate-600 text-sm">Cancel</button>
            </div>

            {uploadError && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{uploadError}</div>
            )}

            {/* Step 1: File drop zone (shown before parsing) */}
            {!parsedPolicy && !uploading && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                  dragOver
                    ? 'border-emerald-400 bg-emerald-50/50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-slate-700 mb-1">
                  Drop your policy document here or click to browse
                </h4>
                <p className="text-xs text-slate-400">
                  Supports PDF and DOCX. The system will read and extract policy metadata automatically.
                </p>
              </div>
            )}

            {/* Parsing indicator */}
            {uploading && (
              <div className="flex flex-col items-center py-12">
                <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4" />
                <h4 className="text-sm font-semibold text-slate-700 mb-1">Reading your policy document...</h4>
                <p className="text-xs text-slate-400">Extracting policy number, title, dates, citations, and content</p>
              </div>
            )}

            {/* Step 2: Review & edit extracted metadata */}
            {parsedPolicy && uploadForm && (
              <div>
                {/* Confidence indicator */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    parsedPolicy.parsed.confidence === 'high'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : parsedPolicy.parsed.confidence === 'medium'
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>
                    {parsedPolicy.parsed.confidence} confidence extraction
                  </span>
                  <span className="text-xs text-slate-400">
                    from {parsedPolicy.file_name} ({Math.round(parsedPolicy.file_size / 1024)} KB)
                  </span>
                </div>

                {parsedPolicy.duplicate_warning && (
                  <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                    {parsedPolicy.duplicate_warning}
                  </div>
                )}

                {/* Extracted sections preview */}
                {parsedPolicy.parsed.key_sections?.length > 0 && (
                  <div className="mb-4 p-3 rounded-xl bg-blue-50/50 border border-blue-100">
                    <span className="text-xs font-medium text-blue-700">Sections detected: </span>
                    <span className="text-xs text-blue-600">
                      {parsedPolicy.parsed.key_sections.join(' · ')}
                    </span>
                  </div>
                )}

                <p className="text-xs text-slate-500 mb-4">Review and edit the extracted information below, then confirm to save.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Policy Number *</label>
                    <input type="text" value={uploadForm.policy_number}
                      onChange={e => setUploadForm(f => ({ ...f, policy_number: e.target.value }))}
                      placeholder="POL-2026-001"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Title *</label>
                    <input type="text" value={uploadForm.title}
                      onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                    <textarea value={uploadForm.description}
                      onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))} rows={2}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
                    <select value={uploadForm.category}
                      onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Revision / Version</label>
                    <input type="text" value={uploadForm.revision_number || ''}
                      onChange={e => setUploadForm(f => ({ ...f, revision_number: e.target.value }))}
                      placeholder="v3.0 / Rev 2.1"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Effective Date</label>
                    <input type="date" value={uploadForm.effective_date || ''}
                      onChange={e => setUploadForm(f => ({ ...f, effective_date: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${uploadForm._ai_approved_by ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        title={uploadForm._ai_approved_by ? 'Extracted from document' : 'Default (uploader). Edit if needed.'} />
                      Approved By
                    </label>
                    <input type="text" value={uploadForm.approved_by_name || ''}
                      onChange={e => setUploadForm(f => ({ ...f, approved_by_name: e.target.value, _ai_approved_by: false }))}
                      placeholder="Name or title"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  {(() => {
                    const dept = departments.find(d => d.id === uploadForm.department_id);
                    const isCompliance = (dept?.name || '').toLowerCase() === 'compliance';
                    return (
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${uploadForm._ai_approval_date ? 'bg-emerald-500' : 'bg-slate-300'}`}
                            title={uploadForm._ai_approval_date ? 'Extracted from document' : (isCompliance ? 'Board of Directors approval — manual entry required' : 'Default (today). Edit if needed.')} />
                          {isCompliance ? 'Board Approval Date' : 'Approval Date'}
                        </label>
                        <input type="date" value={uploadForm.approval_date || ''}
                          onChange={e => setUploadForm(f => ({ ...f, approval_date: e.target.value, _ai_approval_date: false }))}
                          required={isCompliance}
                          className={`w-full rounded-xl border px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                            isCompliance && !uploadForm.approval_date
                              ? 'border-amber-300 bg-amber-50'
                              : 'border-slate-200'
                          }`} />
                        {isCompliance && (
                          <p className={`text-[11px] mt-1.5 px-2 py-1 rounded-md ${
                            uploadForm.approval_date
                              ? 'text-slate-500'
                              : 'text-amber-800 bg-amber-50 border border-amber-200'
                          }`}>
                            Compliance policies require the date the <span className="font-semibold">Board of Directors</span> approved the policy. Enter this manually — it is not auto-filled.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Expiration Date</label>
                    <input type="date" value={uploadForm.expiration_date || ''}
                      onChange={e => setUploadForm(f => ({ ...f, expiration_date: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Supersedes (Policy #)</label>
                    <input type="text" value={uploadForm.supersedes_policy_number || ''}
                      onChange={e => setUploadForm(f => ({ ...f, supersedes_policy_number: e.target.value }))}
                      placeholder="Previous policy number"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${uploadForm._ai_department ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        title={uploadForm._ai_department ? 'Matched from document' : 'Pick from the list'} />
                      Department
                    </label>
                    <select value={uploadForm.department_id}
                      onChange={e => setUploadForm(f => ({ ...f, department_id: e.target.value || '', owner_user_id: '', _ai_department: false }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Org-wide</option>
                      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    {departmentHint?.failed && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md mt-1.5 px-2 py-1">
                        AI extracted <span className="font-semibold">"{departmentHint.name}"</span> but no matching department was found. Please pick one above.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Policy Owner</label>
                    {users.length === 0 ? (
                      // Org has zero users — the only path forward is the
                      // Registration / People module. Tell them so explicitly.
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                        No users available. Have a Superuser create one via the
                        <span className="font-semibold"> Registration / People module</span>, then re-open this upload.
                      </div>
                    ) : eligibleOwners.list.length === 0 ? (
                      // Department was matched but it has no users in it.
                      // Same tooltip / call-to-action.
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                        No users in this department. Have a Superuser add one in the
                        <span className="font-semibold"> Registration / People module</span>,
                        or pick a different department.
                      </div>
                    ) : (
                      <>
                        <select value={uploadForm.owner_user_id}
                          onChange={e => setUploadForm(f => ({ ...f, owner_user_id: e.target.value || '' }))}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                          <option value="">
                            {eligibleOwners.scope === 'department'
                              ? 'Select an owner from this department'
                              : 'Select an owner'}
                          </option>
                          {eligibleOwners.list.map(u => (
                            <option key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ''}</option>
                          ))}
                        </select>
                        <p className="text-[11px] text-slate-400 mt-1">
                          {eligibleOwners.scope === 'department'
                            ? `${eligibleOwners.list.length} user${eligibleOwners.list.length === 1 ? '' : 's'} in this department.`
                            : `Showing all ${eligibleOwners.list.length} active org users.`}
                        </p>
                      </>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Review Cycle (months)</label>
                    <input type="number" min={1} max={60} value={uploadForm.review_cycle_months}
                      onChange={e => setUploadForm(f => ({ ...f, review_cycle_months: parseInt(e.target.value) || 12 }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>

                  {/* Template Detection — agentic flexibility to client formats */}
                  {parsedPolicy?.template_detection && (
                    <div className="md:col-span-2">
                      <div className={`rounded-xl border p-4 ${
                        parsedPolicy.template_detection.recommended_action === 'save_new'
                          ? 'bg-amber-50 border-amber-200'
                          : parsedPolicy.template_detection.recommended_action === 'use_existing'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}>
                        <div className="flex items-start gap-2 mb-3">
                          {parsedPolicy.template_detection.recommended_action === 'save_new'
                            ? <DocumentPlusIcon className="w-5 h-5 text-slate-600 flex-shrink-0" strokeWidth={1.75} />
                            : <DocumentDuplicateIcon className="w-5 h-5 text-slate-600 flex-shrink-0" strokeWidth={1.75} />}
                          <div className="flex-1">
                            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide mb-0.5">
                              Policy Template
                            </h4>
                            <p className="text-xs text-slate-600">
                              {parsedPolicy.template_detection.best_match
                                ? `Closest known template: ${parsedPolicy.template_detection.best_match.name} (${Math.round(parsedPolicy.template_detection.best_match.similarity * 100)}% match)`
                                : parsedPolicy.template_detection.known_templates_count === 0
                                  ? 'No templates saved yet for your organization. Save this format as your first template?'
                                  : 'This policy format does not match any saved template for your organization.'}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {parsedPolicy.template_detection.best_match && (
                            <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-white/60">
                              <input type="radio" name="tdecision" className="mt-0.5"
                                checked={templateDecision?.action === 'use_existing'}
                                onChange={() => setTemplateDecision({ action: 'use_existing', template_id: parsedPolicy.template_detection.best_match.id })} />
                              <span className="text-xs text-slate-700">
                                <span className="font-semibold">Use existing template:</span> {parsedPolicy.template_detection.best_match.name}
                              </span>
                            </label>
                          )}
                          <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-white/60">
                            <input type="radio" name="tdecision" className="mt-0.5"
                              checked={templateDecision?.action === 'save_new'}
                              onChange={() => setTemplateDecision({ action: 'save_new' })} />
                            <div className="flex-1">
                              <span className="text-xs text-slate-700 font-semibold">Save as new template</span>
                              {templateDecision?.action === 'save_new' && (
                                <input type="text" value={newTemplateName}
                                  onChange={e => setNewTemplateName(e.target.value)}
                                  placeholder="e.g., Compliance Policy Template v2"
                                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                              )}
                            </div>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-white/60">
                            <input type="radio" name="tdecision" className="mt-0.5"
                              checked={templateDecision?.action === 'one_time'}
                              onChange={() => setTemplateDecision({ action: 'one_time' })} />
                            <span className="text-xs text-slate-700">
                              <span className="font-semibold">One-time</span> — don't save the format for reuse
                            </span>
                          </label>
                        </div>

                        {parsedPolicy.template_detection.detected?.sections?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Detected structure</div>
                            <div className="flex flex-wrap gap-1">
                              {parsedPolicy.template_detection.detected.sections.slice(0, 12).map((s, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-md text-[10px] bg-white text-slate-600 border border-slate-200">{s}</span>
                              ))}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1">Metadata layout: {parsedPolicy.template_detection.detected.metadata_layout}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CFR Citations */}
                  {uploadForm.cfr_citations?.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Regulatory Citations (auto-detected)</label>
                      <div className="flex flex-wrap gap-1.5">
                        {uploadForm.cfr_citations.map((cite, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-lg text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200">
                            {cite}
                          </span>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">These citations will be auto-linked to regulatory mandates.</p>
                    </div>
                  )}

                  {/* Key sections detected (read-only visual) */}
                  {Array.isArray(uploadForm.key_sections) && uploadForm.key_sections.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Detected Sections ({uploadForm.key_sections.length})</label>
                      <div className="flex flex-wrap gap-1.5">
                        {uploadForm.key_sections.map((s, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-md text-xs bg-slate-50 text-slate-600 border border-slate-200">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Org custom fields — EVERY field from Field Config (not just
                      populate_from_upload). AI-prefilled values are visually marked. */}
                  {parsedPolicy?.parsed?.org_field_definitions?.length > 0 && (
                    <div className="md:col-span-2">
                      <div className="border-t border-slate-100 pt-4 mt-2">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                            Organization custom fields ({parsedPolicy.parsed.org_field_definitions.length})
                          </h4>
                          <div className="flex gap-3 text-[10px] text-slate-400">
                            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" /> AI-prefilled</span>
                            <span><span className="inline-block w-2 h-2 rounded-full bg-slate-300 mr-1" /> Manual entry</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {parsedPolicy.parsed.org_field_definitions.map(fd => {
                            const val = orgFieldInputs[fd.id] || '';
                            const onChange = (v) => setOrgFieldInputs(prev => ({ ...prev, [fd.id]: v }));
                            const isAiField = fd.populate_from_upload;
                            const hasValue = val != null && String(val).trim() !== '';
                            const commonBase = "w-full rounded-xl border px-3 py-2.5 text-slate-800 text-sm focus:ring-2";
                            const cls = `${commonBase} ${
                              isAiField && hasValue
                                ? 'border-emerald-300 bg-emerald-50/40 focus:ring-emerald-500 focus:border-emerald-500'
                                : 'border-slate-200 focus:ring-orange-500 focus:border-orange-500'
                            }`;
                            let opts = fd.options;
                            if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch {} }
                            return (
                              <div key={fd.id}>
                                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${isAiField ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                  <span>{fd.display_label}</span>
                                  {fd.required && <span className="text-red-500">*</span>}
                                  {fd.filterable && <span className="text-[10px] text-slate-400 font-normal">(filterable)</span>}
                                </label>
                                {fd.extraction_hint && (
                                  <div className="text-[10px] text-slate-400 italic mb-1">{fd.extraction_hint}</div>
                                )}
                                {fd.field_type === 'textarea' ? (
                                  <textarea value={val} onChange={e => onChange(e.target.value)} rows={2} className={cls} />
                                ) : fd.field_type === 'select' && Array.isArray(opts) ? (
                                  <select value={val} onChange={e => onChange(e.target.value)} className={cls}>
                                    <option value="">—</option>
                                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                ) : fd.field_type === 'boolean' ? (
                                  <select value={val} onChange={e => onChange(e.target.value)} className={cls}>
                                    <option value="">—</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                  </select>
                                ) : fd.field_type === 'date' ? (
                                  <input type="date" value={val} onChange={e => onChange(e.target.value)} className={cls} />
                                ) : fd.field_type === 'number' ? (
                                  <input type="number" value={val} onChange={e => onChange(e.target.value)} className={cls} />
                                ) : (
                                  <input type="text" value={val} onChange={e => onChange(e.target.value)} className={cls} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2">
                          Every field configured in Field Config appears here. Green-dot fields were auto-populated by AI from the document; edit any value before saving.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Content preview */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Policy Content <span className="text-slate-400 font-normal">({uploadForm.content?.length || 0} chars extracted)</span>
                    </label>
                    <textarea value={uploadForm.content}
                      onChange={e => setUploadForm(f => ({ ...f, content: e.target.value }))} rows={6}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 text-xs font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-2 flex gap-3 pt-2">
                    <button onClick={handleConfirmUpload} disabled={saving}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                      {saving ? 'Saving...' : 'Confirm & Save Policy'}
                    </button>
                    <button onClick={cancelUpload}
                      className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Form */}
        {showCreate && (
          <div className="mb-6 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Create New Policy</h3>
            {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>}
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Policy Number *</label>
                <input type="text" value={form.policy_number} onChange={e => setForm(f => ({ ...f, policy_number: e.target.value }))} placeholder="POL-2026-001"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Part D Payment Reconciliation Procedures"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="What this policy covers..."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Department</label>
                <select value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value || '' }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  <option value="">Org-wide</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Policy Owner</label>
                <select value={form.owner_user_id} onChange={e => setForm(f => ({ ...f, owner_user_id: e.target.value || '' }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  <option value="">Assign later</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Review Cycle (months)</label>
                <input type="number" min={1} max={60} value={form.review_cycle_months} onChange={e => setForm(f => ({ ...f, review_cycle_months: parseInt(e.target.value) || 12 }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
              </div>
              <div className="md:col-span-2 flex gap-3 pt-2">
                <button type="submit" disabled={creating}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                  {creating ? 'Creating...' : 'Create Policy'}
                </button>
                <button type="button" onClick={() => { setShowCreate(false); setError(''); }}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
          <div className="flex gap-3 flex-wrap items-center mb-3">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by title, number, or description..."
              className="flex-1 min-w-[200px] rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
            {activeFilterCount > 0 && (
              <button onClick={clearFilters}
                className="text-xs text-slate-500 hover:text-slate-700 font-medium px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="">All statuses</option>
              {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="">All categories</option>
              {(filterOptions.categories || []).map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="">All departments</option>
              {(filterOptions.departments || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="">All owners</option>
              {(filterOptions.owners || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select value={reviewStateFilter} onChange={e => setReviewStateFilter(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="">Any review state</option>
              <option value="overdue">Review overdue</option>
              <option value="due_soon">Review due soon (30d)</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          {/* Effective date range + org custom filters */}
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="col-span-2 grid grid-cols-2 gap-2">
              <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)}
                placeholder="Effective from"
                className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600" />
              <input type="date" value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)}
                placeholder="Effective to"
                className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600" />
            </div>
            {(filterOptions.filterable_fields || []).map(f => {
              let opts = f.options;
              if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch {} }
              const useSelect = Array.isArray(opts) && opts.length > 0;
              const useDistinct = !useSelect && Array.isArray(f.distinct_values) && f.distinct_values.length > 0 && f.distinct_values.length <= 30;
              const currentVal = orgFieldFilters[f.field_key] || '';
              const onChange = (v) => setOrgFieldFilters(prev => ({ ...prev, [f.field_key]: v }));
              if (useSelect || useDistinct) {
                const options = useSelect ? opts : f.distinct_values;
                return (
                  <select key={f.id} value={currentVal} onChange={e => onChange(e.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600">
                    <option value="">All {f.display_label}</option>
                    {options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                );
              }
              return (
                <input key={f.id} type="text" value={currentVal} onChange={e => onChange(e.target.value)}
                  placeholder={`Filter by ${f.display_label}`}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600" />
              );
            })}
          </div>

        </div>

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse">
                <div className="flex gap-4"><div className="w-16 h-4 bg-slate-100 rounded" /><div className="flex-1 h-4 bg-slate-100 rounded w-2/3" /></div>
              </div>
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center text-orange-600">
              <BookOpenIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">{search ? 'No policies match your search' : 'No policies yet'}</h3>
            <p className="text-sm text-slate-400 mb-4">{search ? 'Try a different search term.' : 'Import your policy manual to get started.'}</p>
            {/* Empty-state CTA routes to Bulk Import — single Upload hidden per
                operator request 2026-07-20 (see header button above). */}
            {!search && (
              <button onClick={() => { setShowBulkImport(true); setShowUpload(false); setShowCreate(false); }}
                className="px-5 py-2.5 rounded-xl border-2 border-orange-500 text-orange-700 text-sm font-semibold hover:bg-orange-50 transition-colors">
                Bulk Import Your Policies
              </button>
            )}
          </div>
        )}

        {/* Policy List — Spreadsheet view */}
        {!loading && filtered.length > 0 && (() => {
          // Columns: Policy ID + Title always; the rest honor the column picker.
          // Org custom-field columns render only when 'org_fields' is toggled on.
          const show = (k) => visibleCols.includes(k);
          const orgFieldColumns = show('org_fields') ? (filterOptions.filterable_fields || []) : [];
          const formatDate = (d) => {
            if (!d) return '—';
            try {
              const dt = new Date(d);
              if (isNaN(dt.getTime())) return String(d);
              return dt.toISOString().slice(0, 10);
            } catch { return String(d); }
          };
          const truncate = (s, n = 40) => (s && s.length > n ? s.slice(0, n) + '…' : s);

          return (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-end px-4 py-2 border-b border-slate-100 relative">
                <button onClick={() => setColPickerOpen(o => !o)}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                  Columns ({2 + visibleCols.length - (show('org_fields') ? 1 : 0) + orgFieldColumns.length})
                </button>
                {colPickerOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setColPickerOpen(false)} />
                    <div className="absolute right-4 top-11 z-20 bg-white rounded-xl border border-slate-200 shadow-xl p-3 w-56">
                      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Show columns</div>
                      <div className="text-xs text-slate-400 mb-2">Policy ID and Title are always shown.</div>
                      {COLUMN_DEFS.map(c => (
                        <label key={c.key} className="flex items-center gap-2 py-1 text-sm text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={visibleCols.includes(c.key)} onChange={() => toggleColumn(c.key)} className="accent-orange-600" />
                          {c.label}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3 whitespace-nowrap">Policy ID</th>
                      <th className="px-4 py-3 whitespace-nowrap">Policy Title</th>
                      {show('status') && <th className="px-4 py-3 whitespace-nowrap">Status</th>}
                      {show('category') && <th className="px-4 py-3 whitespace-nowrap">Category</th>}
                      {show('department') && <th className="px-4 py-3 whitespace-nowrap">Dept. Owner</th>}
                      {show('owner') && <th className="px-4 py-3 whitespace-nowrap">Policy Owner</th>}
                      {show('effective') && <th className="px-4 py-3 whitespace-nowrap">Effective</th>}
                      {show('revision') && <th className="px-4 py-3 whitespace-nowrap">Revision</th>}
                      {show('next_review') && <th className="px-4 py-3 whitespace-nowrap">Next Review</th>}
                      {show('approval') && <th className="px-4 py-3 whitespace-nowrap">Approval</th>}
                      {orgFieldColumns.map(f => (
                        <th key={f.id} className="px-4 py-3 whitespace-nowrap">{f.display_label}</th>
                      ))}
                      {show('file') && <th className="px-4 py-3 whitespace-nowrap text-right">File</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(policy => {
                      const status = STATUS_CFG[policy.status] || STATUS_CFG.draft;
                      const approvalDate = policy.approval_date || policy.approved_at || null;
                      return (
                        <tr
                          key={policy.id}
                          onClick={() => onOpenPolicy(policy.id)}
                          className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">{policy.policy_number || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-800 leading-tight">{policy.title}</div>
                            {policy.description && (
                              <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-1 max-w-md">{policy.description}</div>
                            )}
                            <div className="flex items-center gap-1.5 mt-1">
                              {policy.review_overdue && (
                                <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-red-50 text-red-700 border border-red-200">
                                  Review overdue
                                </span>
                              )}
                              {policy.expired && (
                                <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-red-100 text-red-800 border border-red-300">
                                  Expired
                                </span>
                              )}
                            </div>
                          </td>
                          {show('status') && (
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${status.color}`}>{status.label}</span>
                            </td>
                          )}
                          {show('category') && <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 capitalize">{policy.category || '—'}</td>}
                          {show('department') && <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{policy.department_name || '—'}</td>}
                          {show('owner') && <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{policy.owner_name || '—'}</td>}
                          {show('effective') && <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{formatDate(policy.effective_date)}</td>}
                          {show('revision') && <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-slate-500">{policy.revision_number || (policy.version_count > 0 ? `v${policy.version_count}` : '—')}</td>}
                          {show('next_review') && <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{formatDate(policy.next_review_date)}</td>}
                          {show('approval') && (
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                              {formatDate(approvalDate)}
                              {policy.approved_by_name && (
                                <div className="text-[10px] text-slate-400 leading-tight">{policy.approved_by_name}</div>
                              )}
                            </td>
                          )}
                          {orgFieldColumns.map(f => (
                            <td key={f.id} className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                              {(policy.org_fields && policy.org_fields[f.field_key])
                                ? truncate(String(policy.org_fields[f.field_key]), 30)
                                : '—'}
                            </td>
                          ))}
                          {show('file') && (
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            {policy.source_file_name ? (
                              <div className="inline-flex items-center gap-1.5">
                                {/* View — PDFs render natively in the browser;
                                    DOCX/DOC is converted to HTML server-side
                                    via mammoth and rendered in a styled tab.
                                    Unknown types fall back to download only. */}
                                {/\.(pdf|docx?)$/i.test(policy.source_file_name) && (
                                  <a href={`${API}/api/ppm/policies/${policy.id}/file?inline=1`}
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`View ${policy.source_file_name}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    View
                                  </a>
                                )}
                                <a href={`${API}/api/ppm/policies/${policy.id}/file`}
                                  onClick={(e) => e.stopPropagation()}
                                  download={policy.source_file_name}
                                  title={`Download ${policy.source_file_name}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download
                                </a>
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-300">—</span>
                            )}
                            {policy.status === 'archived' && (
                              <button type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Remove "${policy.title}" for good? Only archived policies can be removed; its files and history go with it.`)) return;
                                  const d = await fetch(`${API}/api/ppm/policies/${policy.id}`, { method: 'DELETE', credentials: 'include' }).then(r => r.json()).catch(() => ({}));
                                  if (d.success) window.location.reload();
                                  else window.alert(d.message || 'Could not remove the policy.');
                                }}
                                className="ml-2 inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                                Delete
                              </button>
                            )}
                          </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-slate-50/50 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
                Showing {filtered.length} {filtered.length === 1 ? 'policy' : 'policies'}
              </div>
            </div>
          );
        })()}

        <BulkImport
          open={showBulkImport}
          onClose={() => setShowBulkImport(false)}
          onImported={load}
        />
      </div>
    </div>
  );
}
